/**
 * FieldHelp - Contextual help icon with tooltip
 *
 * Usage:
 *   <label>
 *     Region
 *     <FieldHelp id="settings.organization.region" />
 *   </label>
 *
 * Features:
 * - Automatic content from i18n
 * - Role-based visibility
 * - Complexity-based trigger (hover/click)
 * - WCAG 2.1 compliant
 * - data-help-id for onboarding integration
 */

import * as React from 'react';
import { HelpCircle, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TooltipRoot, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useTooltip } from './HelpProvider';
import type { TooltipId } from '@/content/help/registry';
import { getDocsUrl } from '@/lib/docs';
import { cn } from '@/lib/utils';
import { CONTROL_SQUARE } from '@/components/ui/control-size';

interface FieldHelpProps {
  /**
   * Tooltip ID from registry
   */
  id: TooltipId;

  /**
   * Icon size
   * @default "sm"
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Custom class name
   */
  className?: string;

  /**
   * Force show even if content is missing (for development)
   */
  forceShow?: boolean;

  /**
   * Highlight for onboarding
   */
  highlight?: boolean;
}

const SIZE_MAP = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

export function FieldHelp({
  id,
  size = 'sm',
  className,
  forceShow = false,
  highlight = false,
}: FieldHelpProps) {
  const { t, i18n } = useTranslation('common');
  const { meta, content, isVisible, triggerConfig } = useTooltip(id);

  // Don't render if no content or not visible for role
  if (!content && !forceShow) return null;
  if (!isVisible) return null;

  const hasLearnMore = meta?.learnMorePath && triggerConfig?.showLearnMore;

  return (
    <TooltipRoot delayDuration={triggerConfig?.delayMs ?? 200}>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-help-id={id}
          className={cn(
            'inline-flex items-center justify-center rounded-full',
            'text-fg-muted hover:text-fg transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            // 32px hit area (WCAG 2.2 AA clears 24px), down from 44px. The negative
            // margin is what the layout sees: 32px - 4px = 28px, the slot the old
            // 44px box (-m-2) took, so the neighbouring label does not move.
            CONTROL_SQUARE.sm,
            '-m-0.5',
            // Highlight animation for onboarding
            highlight && 'animate-pulse ring-2 ring-accent ring-offset-2',
            className
          )}
          aria-label={content?.title ?? t('common.moreInformation')}
          aria-describedby={`help-${id}`}
        >
          <HelpCircle className={cn(SIZE_MAP[size], 'shrink-0')} />
        </button>
      </TooltipTrigger>

      <TooltipPrimitive.Portal>
        <TooltipContent
          id={`help-${id}`}
          side="top"
          align="start"
          className={cn(
            'max-w-xs p-3',
            'bg-bg-elevated border-border',
            // More padding for rich content
            meta?.isRich && 'max-w-sm p-4'
          )}
          // WCAG: Persist on hover, escape to dismiss
          onPointerDownOutside={(e) => {
            // Allow clicking learn more link
            if ((e.target as HTMLElement)?.closest('a')) {
              e.preventDefault();
            }
          }}
        >
          <div>
            {/* Title (optional) */}
            {content?.title && (
              <p className="font-semibold text-fg text-sm mb-1">{content.title}</p>
            )}

            {/* Body (required) */}
            <p className="text-fg-muted text-xs leading-relaxed">{content?.body}</p>

            {/* Details (optional) */}
            {content?.details && (
              <p className="text-fg-muted text-xs leading-relaxed mt-2 opacity-80">
                {content.details}
              </p>
            )}

            {/* Example (optional) */}
            {content?.example && (
              <p className="text-fg-muted text-xs leading-relaxed mt-2 italic border-l-2 border-border pl-2">
                {content.example}
              </p>
            )}

            {/* Learn more link */}
            {hasLearnMore && (
              <a
                href={getDocsUrl(meta.learnMorePath, i18n.language)}
                target="_blank"
                rel={['noopener', 'noreferrer'].join(' ')}
                className={cn(
                  'inline-flex items-center gap-1 mt-3',
                  'text-xs text-accent hover:text-accent transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
              >
                {t('common.moreInformation')}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </TooltipRoot>
  );
}

/**
 * Inline variant - renders as inline text with tooltip
 */
interface FieldHelpInlineProps extends FieldHelpProps {
  children: React.ReactNode;
}

export function FieldHelpInline({ id, children, className }: FieldHelpInlineProps) {
  const { content, isVisible, triggerConfig } = useTooltip(id);

  if (!content || !isVisible) {
    return <>{children}</>;
  }

  return (
    <TooltipRoot delayDuration={triggerConfig?.delayMs ?? 200}>
      <TooltipTrigger asChild>
        <span
          data-help-id={id}
          className={cn(
            'cursor-help border-b border-dotted border-fg-muted',
            'hover:border-fg transition-colors',
            className
          )}
        >
          {children}
        </span>
      </TooltipTrigger>

      <TooltipPrimitive.Portal>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-xs p-3 bg-bg-elevated border-border"
        >
          <div>
            {content.title && (
              <p className="font-semibold text-fg text-sm mb-1">{content.title}</p>
            )}
            <p className="text-fg-muted text-xs leading-relaxed">{content.body}</p>
            {content.details && (
              <p className="text-fg-muted text-xs leading-relaxed mt-2 opacity-80">
                {content.details}
              </p>
            )}
          </div>
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </TooltipRoot>
  );
}

export default FieldHelp;
